import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';
import { photoUrl, type TripPhoto } from '@/hooks/useTripPhotos';
import { AuthedImage, viewFile } from '@/components/common/AuthedImage';

interface PhotoLightboxProps {
  open: boolean;
  onClose: () => void;
  photos: TripPhoto[];
  startIndex: number;
}

export function PhotoLightbox({ open, onClose, photos, startIndex }: PhotoLightboxProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => { if (open) setIndex(startIndex); setZoom(1); }, [open, startIndex]);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % photos.length);
    setZoom(1);
  }, [photos.length]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + photos.length) % photos.length);
    setZoom(1);
  }, [photos.length]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z + 0.25));
      else if (e.key === '-') setZoom((z) => Math.max(0.5, z - 0.25));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, next, prev]);

  if (!open || !photos.length) return null;
  const photo = photos[index];

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between p-3 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 text-sm">
          <span className="font-mono opacity-75">{t(`photos.kind.${photo.kind}`)}</span>
          <span className="opacity-50">·</span>
          <span>{index + 1} / {photos.length}</span>
          {photo.exifTakenAt && (
            <>
              <span className="opacity-50">·</span>
              <span className="opacity-75">{new Date(photo.exifTakenAt).toLocaleString()}</span>
            </>
          )}
          {photo.exifLat !== null && photo.exifLng !== null && (
            <>
              <span className="opacity-50">·</span>
              <a
                href={`https://www.google.com/maps?q=${photo.exifLat},${photo.exifLng}`}
                target="_blank" rel="noreferrer"
                className="opacity-75 hover:opacity-100 underline"
                onClick={(e) => e.stopPropagation()}
              >
                {photo.exifLat?.toFixed(4)}°, {photo.exifLng?.toFixed(4)}°
              </a>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(0.5, z - 0.25)); }}
            className="p-2 hover:bg-white/10 rounded" title={t('photos.zoomOut')}
          ><ZoomOut className="h-4 w-4" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); setZoom(1); }}
            className="p-2 hover:bg-white/10 rounded" title={t('photos.resetZoom')}
          ><Maximize2 className="h-4 w-4" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(4, z + 0.25)); }}
            className="p-2 hover:bg-white/10 rounded" title={t('photos.zoomIn')}
          ><ZoomIn className="h-4 w-4" /></button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); viewFile(`/api/v1/trips/${photo.tripId}/photos/${photo.id}`).catch(() => {}); }}
            className="p-2 hover:bg-white/10 rounded inline-block" title={t('photos.download')}
          ><Download className="h-4 w-4" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-2 hover:bg-white/10 rounded ms-2" title={t('common.close')}
          ><X className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-auto" onClick={(e) => e.stopPropagation()}>
        <AuthedImage
          src={photoUrl(photo.tripId, photo.id)}
          alt={t(`photos.kind.${photo.kind}`)}
          className="max-h-full max-w-full select-none"
          style={{ transform: `scale(${zoom})`, transition: 'transform 150ms ease' }}
          draggable={false}
          loading="eager"
        />
      </div>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute start-2 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
          ><ChevronLeft className="h-6 w-6" /></button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute end-2 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
          ><ChevronRight className="h-6 w-6" /></button>
        </>
      )}
    </div>
  );
}
